export type SentinelStatus = "queued" | "scanning" | "pass" | "warn" | "fail";
export type SentinelScore = "A" | "A-" | "B" | "Fail";
export type SentinelImpact = "none" | "convenience" | "hardening" | "fail";

export type SentinelCheck = {
  id: string;
  label: string;
  status: SentinelStatus;
  evidence: string;
  details?: string;
  impact: SentinelImpact;
};

export const allowedStorageKeys = new Set([
  "envship-theme",
  "envship-dashboard-flow",
  "envship-unlock-session",
  "envship-dashboard-editor-encrypted-keyfile-v1"
]);

export const suspiciousStoragePattern = /(private[_-]?key|private_keyset|plaintext|dotenv|env[_-]?value|secret|passphrase|decrypted)/i;

export function isLocalTrustedDevHost(hostname: string) {
  return ["envship.test", "qrlink.test", "localhost", "127.0.0.1"].includes(hostname);
}

export function isAllowedPlatformScript(src: string, appOrigin: string) {
  try {
    const parsed = new URL(src);
    if (parsed.origin === appOrigin) return true;
    return parsed.origin === "https://static.cloudflareinsights.com" && parsed.pathname.startsWith("/beacon.min.js");
  } catch {
    return false;
  }
}

export function classifySentinelResults(checks: SentinelCheck[]): { score: SentinelScore; summary: string } {
  if (checks.some((check) => check.status === "fail")) {
    return {
      score: "Fail",
      summary: "A required browser safety check failed. Fix the issue or use the CLI before handling KeyFiles."
    };
  }
  if (checks.some((check) => check.status === "warn" && check.impact === "hardening")) {
    return {
      score: "B",
      summary: "Required checks passed. Review the hardening item before production key use."
    };
  }
  if (checks.some((check) => check.status === "warn" && check.impact === "convenience")) {
    return {
      score: "A-",
      summary: "Local checks passed. Encrypted browser persistence is convenient, but lower assurance than memory-only key use."
    };
  }
  return {
    score: "A",
    summary: "Baseline checks passed. Extension and ad-blocker reminders are trust-boundary notes, not score penalties."
  };
}

export function isSuspiciousStorageKey(storageKey: string) {
  return suspiciousStoragePattern.test(storageKey);
}

export function isUnexpectedEnvShipStorageKey(storageKey: string) {
  const keyName = storageKey.replace(/^(local|session):/, "");
  return keyName.startsWith("envship-") && !allowedStorageKeys.has(keyName);
}
